import { z } from 'zod'
import { RIDE_IDS } from './rides.schema'

/** What a catalogue item costs in (gems pending O-22). */
export const CURRENCIES = ['coins', 'gems'] as const

/** The six costume slots (PHASE3 T3.8), in the hub's and the shop's tab order. */
export const COSTUME_SLOTS = ['hat', 'face', 'top', 'bottoms', 'shoes', 'back'] as const

/** Costume rarities: the card's colour, nothing else. */
export const RARITIES = ['common', 'rare', 'epic'] as const

/**
 * One ride in the hub and the shop (PHASE3 T3.11, D-049): the display name, the ability's name,
 * a one-line blurb, and its price. `starter` rides are owned by everyone; the first starter is
 * the default ride. Gameplay numbers stay in `rides.json`.
 */
export const catalogRideSchema = z.strictObject({
  id: z.enum(RIDE_IDS),
  name: z.string().min(1).max(24),
  ability: z.string().min(1).max(16),
  blurb: z.string().min(1).max(64),
  price: z.number().int().min(0),
  currency: z.enum(CURRENCIES),
  starter: z.literal(true).optional(),
})

/**
 * One costume item (PHASE3 T3.8, D-049). Ids are the `rigs.json → costumes` keys, but the
 * loader checks nothing about art (art may lag). The list stays empty until the owner settles
 * the runner cast; the shop (T3.9a) already reads it.
 */
export const catalogCostumeSchema = z.strictObject({
  id: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(32),
  slot: z.enum(COSTUME_SLOTS),
  name: z.string().min(1).max(24),
  price: z.number().int().min(0),
  currency: z.enum(CURRENCIES),
  rarity: z.enum(RARITIES),
  starter: z.literal(true).optional(),
})

/** Shop presentation numbers (T3.9a): how many items the Featured tab picks each UTC day. */
export const catalogShopSchema = z.strictObject({
  featured: z.number().int().min(1).max(12),
})

/**
 * Raw `catalog.json`. Every ride in `rides.json` appears exactly once, and at least one is a
 * starter. `costumes` may be left out (none yet); costume ids are unique.
 */
export const catalogFileSchema = z
  .strictObject({
    rides: z.array(catalogRideSchema),
    costumes: z.array(catalogCostumeSchema).default([]),
    shop: catalogShopSchema,
  })
  .superRefine((f, ctx) => {
    const ids = f.rides.map((r) => r.id)
    for (const id of RIDE_IDS) {
      const n = ids.filter((x) => x === id).length
      if (n !== 1) {
        ctx.addIssue({ code: 'custom', path: ['rides'], message: `${id} appears ${n} times` })
      }
    }
    if (!f.rides.some((r) => r.starter)) {
      ctx.addIssue({ code: 'custom', path: ['rides'], message: 'no starter ride' })
    }
    const seen = new Set<string>()
    for (const c of f.costumes) {
      if (seen.has(c.id)) {
        ctx.addIssue({ code: 'custom', path: ['costumes'], message: `${c.id} appears twice` })
      }
      seen.add(c.id)
    }
  })

export type CatalogFile = z.infer<typeof catalogFileSchema>
export type CatalogRide = CatalogFile['rides'][number]
export type CatalogCostume = CatalogFile['costumes'][number]
export type CostumeSlot = (typeof COSTUME_SLOTS)[number]
export type Rarity = (typeof RARITIES)[number]
